import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaShieldAlt, FaChartBar, FaUsers } from 'react-icons/fa';
import apLogo from '../images/ayu2.png';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);
    setLoading(false);

    if (result.success) {
      const role = localStorage.getItem('authRole');
      if (
        role === 'wallet_finance' ||
        role === 'wallet_ops' ||
        role === 'wallet_support' ||
        role === 'wallet_superadmin'
      ) {
        navigate('/subscriber-wallet');
      } else if (role === 'finance_user' || role === 'tp_admin') {
        navigate('/financedashboard');
      } else if (role === 'internal_user') {
        navigate('/dashboard/subscription');
      } else {
        navigate('/unauthorized');
      }
    } else {
      setError(result.message);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── Left panel: AyushPay navy brand background ── */}
      <div
        className="hidden lg:flex lg:w-6/12 flex-col items-center justify-center p-12 relative overflow-hidden "
        style={{ background: 'linear-gradient(160deg, #0d1f4e 0%, #0f2c6b 60%, #0e3a7d 100%)' }}
      >
        {/* Subtle glow blobs matching brand teal */}
        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10 -translate-y-1/2 translate-x-1/3"
          style={{ background: 'radial-gradient(circle, #22d3ee, transparent)' }}
        />
        <div
          className="absolute bottom-0 left-0 w-72 h-72 rounded-full opacity-10 translate-y-1/3 -translate-x-1/3"
          style={{ background: 'radial-gradient(circle, #22d3ee, transparent)' }}
        />

        <div className="relative z-10 flex flex-col items-center text-center max-w-xs w-full">
          {/* Logo — white version readable on navy */}
          <div className="rounded-2xl px-6 py-4 mb-4 ">
            <img
              src={apLogo}
              alt="AyushPay"
              className="h-10 w-auto select-none"
              draggable={false}
            />
          </div>

          <h2 className="text-2xl font-bold text-white mb-3 leading-tight">
            Admin Portal
          </h2>
          <p className="text-blue-200 text-sm leading-relaxed mb-10 opacity-80">
            Manage subscribers, transactions, and operations from one unified dashboard.
          </p>

          {/* Feature cards */}
          <div className="w-full space-y-2.5">
            {[
              { icon: FaUsers,     label: 'Subscriber Management', sub: 'View & manage all subscribers'     },
              { icon: FaChartBar,  label: 'Analytics & Reports',   sub: 'Track payments and performance'    },
              { icon: FaShieldAlt, label: 'Role-based Access',     sub: 'Secure, permission-driven access'  },
            ].map(({ icon: Icon, label, sub }) => (
              <div
                key={label}
                className="flex items-center gap-3.5 rounded-xl px-4 py-3 text-left"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(34,211,238,0.2)', color: '#22d3ee' }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-white">{label}</p>
                  <p className="text-xs leading-tight mt-0.5 text-blue-200 opacity-70">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel: form ── */}
      <div className="flex-1 flex items-center justify-center p-6 mt-[110px] ">
        <div className="w-full max-w-sm">

          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src={apLogo}
              alt="AyushPay"
              className="h-8 w-auto mx-auto select-none"
              draggable={false}
            />
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900">Welcome</h2>
            <p className="text-slate-400 text-sm mt-1">Sign in to your admin account</p>
          </div>

          {/* Error banner */}
          {error && (
            <div className="mb-5 px-4 py-3 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2.5">
              <div className="w-4 h-4 rounded-full bg-rose-400 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold leading-none">!</span>
              </div>
              <p className="text-rose-700 text-sm leading-snug">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Username */}
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                Username
              </label>
              <div className="relative">
                <FaUser className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#94a3b8' }} />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-300
                    hover:border-slate-300 transition-colors shadow-sm"
                  style={{ outline: 'none' }}
                  onFocus={e => { e.target.style.borderColor = '#0ea5e9'; e.target.style.boxShadow = '0 0 0 3px rgba(14,165,233,0.12)'; }}
                  onBlur={e  => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                Password
              </label>
              <div className="relative">
                <FaLock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#94a3b8' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full pl-10 pr-10 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-300
                    hover:border-slate-300 transition-colors shadow-sm"
                  style={{ outline: 'none' }}
                  onFocus={e => { e.target.style.borderColor = '#0ea5e9'; e.target.style.boxShadow = '0 0 0 3px rgba(14,165,233,0.12)'; }}
                  onBlur={e  => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: '#94a3b8' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#475569'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; }}
                >
                  {showPassword ? <FaEyeSlash className="w-3.5 h-3.5" /> : <FaEye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {/* Submit — brand teal/cyan */}
            <button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm mt-2 text-white"
              style={{
                background: loading || !username || !password
                  ? '#7dd3fc'
                  : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                cursor: loading || !username || !password ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Signing in…
                </span>
              ) : 'Sign In'}
            </button>

          </form>

          {/* Footer */}
          <p className="text-center text-xs text-slate-300 mt-8">
            © {new Date().getFullYear()} AyushPay · Admin Portal
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
